import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { clsx } from 'clsx'

export interface SnackBarOptions {
  content: ReactNode
  /** `SnackBarBehavior.floating`: inset card with a 4px radius instead of a full-width bar. */
  floating?: boolean
  /** Milliseconds on screen. Flutter's default is 4 seconds. */
  duration?: number
  action?: { label: string; onPressed: () => void }
}

interface SnackBarApi {
  showSnackBar: (options: SnackBarOptions) => void
  hideCurrentSnackBar: () => void
}

interface Entry {
  id: number
  options: SnackBarOptions
  closing: boolean
}

const DEFAULT_DURATION_MS = 4000
const EXIT_MS = 200

const SnackBarContext = createContext<SnackBarApi | null>(null)

export function useSnackBar(): SnackBarApi {
  const api = useContext(SnackBarContext)
  if (!api) throw new Error('useSnackBar must be used inside a SnackBarProvider')
  return api
}

/** The queue/timer state machine, kept outside React so effects never run inside state updaters. */
function createController(publish: (entry: Entry | null) => void) {
  const queue: Entry[] = []
  let visible: Entry | null = null
  let nextId = 0
  let dismissTimer: ReturnType<typeof setTimeout> | undefined
  let exitTimer: ReturnType<typeof setTimeout> | undefined

  function present(entry: Entry) {
    visible = entry
    publish(entry)
    dismissTimer = setTimeout(hide, entry.options.duration ?? DEFAULT_DURATION_MS)
  }

  function hide() {
    if (!visible || visible.closing) return
    clearTimeout(dismissTimer)
    visible = { ...visible, closing: true }
    publish(visible)
    exitTimer = setTimeout(() => {
      const next = queue.shift()
      if (next) {
        present(next)
      } else {
        visible = null
        publish(null)
      }
    }, EXIT_MS)
  }

  function show(options: SnackBarOptions) {
    const entry: Entry = { id: nextId++, options, closing: false }
    if (visible) queue.push(entry)
    else present(entry)
  }

  function dispose() {
    clearTimeout(dismissTimer)
    clearTimeout(exitTimer)
  }

  return { show, hide, dispose }
}

/**
 * ScaffoldMessenger.showSnackBar: one snack bar at a time, the rest wait in a queue.
 * Pages that sit above the bottom navigation set `--snackbar-bottom-inset` on an ancestor so
 * the bar rests on top of it, as it does in the Flutter shell.
 */
export function SnackBarProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<Entry | null>(null)
  const controller = useRef<ReturnType<typeof createController> | null>(null)
  if (!controller.current) controller.current = createController(setCurrent)
  const ctl = controller.current

  useEffect(() => () => ctl.dispose(), [ctl])

  const api = useMemo<SnackBarApi>(
    () => ({ showSnackBar: ctl.show, hideCurrentSnackBar: ctl.hide }),
    [ctl]
  )

  return (
    <SnackBarContext.Provider value={api}>
      {children}
      {current && <SnackBarView entry={current} onAction={ctl.hide} />}
    </SnackBarContext.Provider>
  )
}

function SnackBarView({ entry, onAction }: { entry: Entry; onAction: () => void }) {
  const { content, floating, action } = entry.options

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx(
        'fixed left-1/2 z-[70] flex w-full -translate-x-1/2 items-center justify-between gap-4 bg-lm-inverse-surface text-lm-surface shadow-elevation-6',
        'bottom-[var(--snackbar-bottom-inset,env(safe-area-inset-bottom))] text-body-md transition-all duration-200',
        entry.closing ? 'translate-y-full opacity-0' : 'animate-slide-up',
        floating
          ? 'mb-[10px] max-w-[450px] w-[calc(100%-30px)] rounded px-4 py-[14px]'
          : 'max-w-[480px] px-6 py-[14px]'
      )}
    >
      <span className="min-w-0 flex-1">{content}</span>
      {action && (
        <button
          type="button"
          className="shrink-0 text-label-lg text-lm-on-primary"
          onClick={() => {
            action.onPressed()
            onAction()
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  )
}
